import {
  createStore,
  emitOnce,
  filterNil,
  select,
  withProps,
} from '@ngneat/elf';
import {
  getAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  clearRequestsStatus,
  createRequestsStatusOperator,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import {
  ConfigurationProfile,
  SpeedCoachingProfile,
  Vehicle,
  VehicleGroup,
} from '@tmx/data-access';
import { RowsDescriptor, SortDirection } from '@tmx/operandi';
import { filter, Subject } from 'rxjs';

import { EMPTY_ROWS_DESCRIPTOR } from '../../../../../models/empty-rows-descriptor';

interface vehicleGroupsProps {
  configurationProfiles: ConfigurationProfile[];
  creatingNewVehicleGroup: boolean;
  defaultConfigurationProfile: null | number;
  defaultSpeedCoachingProfile: null | number;
  hasAllVehiclesGroup: boolean | null;
  hasVehicleGroups: boolean;
  offset: number;
  query: string;
  selectedSpeedCoachingProfileId: null | number;
  selectedVehicleGroup: null | VehicleGroup;
  selectedVehicleGroupConfigurationProfileId: null | number;
  selectedVehicleGroupCustomPlaceIds: null | number[];
  selectedVehicleGroupMapSetIds: null | number[];
  selectedVehicleGroupRouteModifierIds: null | number[];
  selectedVehicleGroupRoutingProfileIds: null | number[];
  selectedVehicleGroupVehicles: null | Vehicle[];
  sortDirection: '' | 'asc' | 'desc';
  sortExpression: string;
  speedCoachingProfiles: SpeedCoachingProfile[];
  totalVehicleGroups: number;
  vehicleGroupErrorMessage: null | string;
  vehicleGroupFilterOptions: VehicleGroup[];
  vehicleGroupsRowsDescriptor: RowsDescriptor;
}

const vehicleGroupsStore = createStore(
  { name: 'vehicleGroups' },
  withProps<vehicleGroupsProps>({
    totalVehicleGroups: 0,
    hasVehicleGroups: false,
    hasAllVehiclesGroup: null,
    offset: 0,
    query: '',
    sortExpression: '',
    sortDirection: SortDirection.None,
    vehicleGroupsRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    selectedVehicleGroup: null,
    selectedVehicleGroupVehicles: [],
    selectedVehicleGroupRoutingProfileIds: [],
    defaultConfigurationProfile: null,
    selectedVehicleGroupConfigurationProfileId: null,
    configurationProfiles: [],
    defaultSpeedCoachingProfile: null,
    selectedSpeedCoachingProfileId: null,
    speedCoachingProfiles: [],
    vehicleGroupErrorMessage: null,
    creatingNewVehicleGroup: false,
    selectedVehicleGroupMapSetIds: [],
    selectedVehicleGroupRouteModifierIds: [],
    selectedVehicleGroupCustomPlaceIds: [],
    vehicleGroupFilterOptions: [],
  }),
  withEntities<VehicleGroup, 'vehicleGroupId'>({
    idKey: 'vehicleGroupId',
  }),
  withRequestsStatus<
    | 'configuration-profile-assignment'
    | 'configuration-profiles'
    | 'create-all-vehicles-group'
    | 'default-configuration-profile'
    | 'default-map-set-assignments'
    | 'default-route-modifier-assignments'
    | 'default-routing-profile-assignments'
    | 'default-speed-coaching-profile'
    | 'delete-vehicle-group'
    | 'edit-selected-vehicle-group-custom-places'
    | 'edit-selected-vehicle-group-map-sets'
    | 'edit-selected-vehicle-group-route-modifiers'
    | 'edit-selected-vehicle-group-routing-profiles'
    | 'edit-selected-vehicle-group-vehicles'
    | 'edit-vehicle-groups'
    | 'load-vehicle-group-filter-options'
    | 'selected-configuration-profile'
    | 'selected-speed-coaching-profile'
    | 'selected-vehicle-group-custom-places'
    | 'selected-vehicle-group-map-sets'
    | 'selected-vehicle-group-route-modifiers'
    | 'selected-vehicle-group-routing-profiles'
    | 'selected-vehicle-group-vehicles'
    | 'speed-coaching-profile-assignment'
    | 'speed-coaching-profiles'
    | 'vehicle-group'
    | 'vehicle-groups'
  >()
);

export const trackVehicleGroupsRequestsStatus =
  createRequestsStatusOperator(vehicleGroupsStore);

export const vehicleGroupErrorMessage$ = vehicleGroupsStore.pipe(
  select((state) => state.vehicleGroupErrorMessage),
  filterNil()
);

export const vehicleGroupsStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('vehicle-groups')
);

export const deleteVehicleGroupStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('delete-vehicle-group')
);

export const vehicleGroupStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('vehicle-group')
);

export const editVehicleGroupsStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('edit-vehicle-groups')
);

export const selectedVehicleGroupVehiclesStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('selected-vehicle-group-vehicles')
);

export const editSelectedVehicleGroupVehiclesStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('edit-selected-vehicle-group-vehicles')
);

export const selectedVehicleGroupRoutingProfilesStatus$ =
  vehicleGroupsStore.pipe(
    selectRequestStatus('selected-vehicle-group-routing-profiles')
  );

export const editSelectedVehicleGroupRoutingProfilesStatus$ =
  vehicleGroupsStore.pipe(
    selectRequestStatus('edit-selected-vehicle-group-routing-profiles')
  );

export const defaultConfigurationProfileStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('default-configuration-profile')
);

export const selectedConfigurationProfileStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('selected-configuration-profile')
);

export const configurationProfilesStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('configuration-profiles')
);

export const configurationProfileAssignmentStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('configuration-profile-assignment')
);

export const defaultSpeedCoachingProfileStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('default-speed-coaching-profile')
);

export const selectedSpeedCoachingProfileStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('selected-speed-coaching-profile')
);

export const speedCoachingProfilesStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('speed-coaching-profiles')
);

export const speedCoachingProfileAssignmentStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('speed-coaching-profile-assignment')
);

export const selectedVehicleGroupMapSetsStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('selected-vehicle-group-map-sets')
);

export const editSelectedVehicleGroupMapSetsStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('edit-selected-vehicle-group-map-sets')
);

export const defaultMapSetAssignmentsStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('default-map-set-assignments')
);

export const selectedVehicleGroupRouteModifiersStatus$ =
  vehicleGroupsStore.pipe(
    selectRequestStatus('selected-vehicle-group-route-modifiers')
  );

export const editSelectedVehicleGroupRouteModifiersStatus$ =
  vehicleGroupsStore.pipe(
    selectRequestStatus('edit-selected-vehicle-group-route-modifiers')
  );

export const selectedVehicleGroupCustomPlacesStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('selected-vehicle-group-custom-places')
);

export const editSelectedVehicleGroupCustomPlacesStatus$ =
  vehicleGroupsStore.pipe(
    selectRequestStatus('edit-selected-vehicle-group-custom-places')
  );

export const loadVehicleGroupFilterOptions = vehicleGroupsStore.pipe(
  selectRequestStatus('load-vehicle-group-filter-options')
);

export const createAllVehiclesGroupStatus$ = vehicleGroupsStore.pipe(
  selectRequestStatus('create-all-vehicles-group')
);

export const hasVehicleGroups$ = vehicleGroupsStore.pipe(
  select((state) => state.hasVehicleGroups)
);

export const hasAllVehiclesGroup$ = vehicleGroupsStore.pipe(
  select(({ hasAllVehiclesGroup }) => hasAllVehiclesGroup),
  filter((hasAllVehiclesGroup) => hasAllVehiclesGroup !== null)
);

export const configurationProfiles$ = vehicleGroupsStore.pipe(
  select((state) => state.configurationProfiles)
);

export const speedCoachingProfiles$ = vehicleGroupsStore.pipe(
  select((state) => state.speedCoachingProfiles)
);

export const vehicleGroupsRowsDescriptor$ = vehicleGroupsStore.pipe(
  select((state) => state.vehicleGroupsRowsDescriptor)
);

export const tableReload$ = new Subject<void>();
export const currentStepReset$ = new Subject<number>();
export const currentStepSave$ = new Subject<number>();

export const vehicleGroupFilterOptions$ = vehicleGroupsStore.pipe(
  select(({ vehicleGroupFilterOptions: allVehicleGroups }) => allVehicleGroups)
);

export const selectedVehicleGroupName$ = vehicleGroupsStore.pipe(
  select(
    ({ selectedVehicleGroup }) => selectedVehicleGroup?.vehicleGroupName ?? null
  )
);

export function updateHasVehicleGroups(hasVehicleGroups: boolean) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    hasVehicleGroups,
  }));
}

export function updateHasAllVehiclesGroup(hasAllVehiclesGroup: boolean) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    hasAllVehiclesGroup,
  }));
}

export function updateVehicleGroups(
  vehicleGroups: VehicleGroup[],
  totalVehicleGroups: number
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      totalVehicleGroups,
    }),
    setEntities(vehicleGroups),
    updateRequestStatus('vehicle-groups', 'success')
  );
}

export function updateSelectedVehicleGroup(
  selectedVehicleGroup: null | VehicleGroup
) {
  emitOnce(() => {
    vehicleGroupsStore.update((state) => ({
      ...state,
      selectedVehicleGroup,
    }));

    if (selectedVehicleGroup === null) {
      vehicleGroupsStore.update(
        (state) => ({
          ...state,
          selectedVehicleGroupRoutingProfileIds: [],
          selectedVehicleGroupVehicles: [],
        }),
        updateRequestStatus('selected-vehicle-group-vehicles', 'idle'),
        updateRequestStatus('default-routing-profile-assignments', 'idle')
      );
    }
  });
}

export function updateSelectedVehicleGroupName(
  selectedVehicleGroupName: string
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    selectedVehicleGroupName,
  }));
}

export function updateSelectedVehicleGroupOorEmail(
  selectedVehicleGroupOorEmail: string
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    selectedVehicleGroupOorEmail,
  }));
}

export function updateSelectedVehicleGroupOorEmailIsEnabled(
  selectedVehicleGroupOorEmailIsEnabled: boolean
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    selectedVehicleGroupOorEmailIsEnabled,
  }));
}

export function updateSelectedVehicleGroupVehicles(
  selectedVehicleGroupVehicles: null | Vehicle[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedVehicleGroupVehicles,
    }),
    updateRequestStatus('selected-vehicle-group-vehicles', 'success')
  );
}

export function updateSelectedVehicleGroupRoutingProfiles(
  selectedVehicleGroupRoutingProfileIds: null | number[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedVehicleGroupRoutingProfileIds,
    }),
    updateRequestStatus('selected-vehicle-group-routing-profiles', 'success')
  );
}

export function updateDefaultConfigurationProfile(
  defaultConfigurationProfile: null | number
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      defaultConfigurationProfile,
    }),
    updateRequestStatus('default-configuration-profile', 'success')
  );
}

export function updateSelectedVehicleGroupConfigurationProfileId(
  selectedVehicleGroupConfigurationProfileId: null | number
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedVehicleGroupConfigurationProfileId,
    }),
    updateRequestStatus('selected-configuration-profile', 'success')
  );
}

export function updateConfigurationProfiles(
  configurationProfiles: ConfigurationProfile[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      configurationProfiles,
    }),
    updateRequestStatus('configuration-profiles', 'success')
  );
}

export function updateDefaultSpeedCoachingProfile(
  defaultSpeedCoachingProfile: null | number
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      defaultSpeedCoachingProfile,
    }),
    updateRequestStatus('default-speed-coaching-profile', 'success')
  );
}

export function updateSelectedSpeedCoachingProfileId(
  selectedSpeedCoachingProfileId: null | number
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedSpeedCoachingProfileId,
    }),
    updateRequestStatus('selected-speed-coaching-profile', 'success')
  );
}

export function updateSpeedCoachingProfiles(
  speedCoachingProfiles: SpeedCoachingProfile[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      speedCoachingProfiles,
    }),
    updateRequestStatus('speed-coaching-profiles', 'success')
  );
}

export function updateVehicleGroupErrorMessage(
  vehicleGroupErrorMessage: null | string
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    vehicleGroupErrorMessage,
  }));
}

export function updateCreatingNewVehicleGroup(
  creatingNewVehicleGroup: boolean
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    creatingNewVehicleGroup,
  }));
}

export function updateCurrentStepReset(currentStep: number) {
  currentStepReset$.next(currentStep);
}

export function updateCurrentStepSave(currentStep: number) {
  currentStepSave$.next(currentStep);
}

export function updateTableReload() {
  tableReload$.next();
}

export function updateSelectedVehicleGroupMapSets(
  selectedVehicleGroupMapSetIds: null | number[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedVehicleGroupMapSetIds,
    }),
    updateRequestStatus('selected-vehicle-group-map-sets', 'success')
  );
}

export function updateSelectedVehicleGroupRouteModifiers(
  selectedVehicleGroupRouteModifierIds: null | number[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedVehicleGroupRouteModifierIds,
    }),
    updateRequestStatus('selected-vehicle-group-route-modifiers', 'success')
  );
}

export function updateSelectedVehicleGroupCustomPlaces(
  selectedVehicleGroupCustomPlaceIds: null | number[]
) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      selectedVehicleGroupCustomPlaceIds,
    }),
    updateRequestStatus('selected-vehicle-group-custom-places', 'success')
  );
}

export function updateVehicleGroupStatus() {
  vehicleGroupsStore.update(updateRequestStatus('vehicle-group', 'success'));
}

export function updateEditVehicleGroupsStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('edit-vehicle-groups', 'success')
  );
}

export function updateSelectedVehicleGroupVehiclesStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('edit-selected-vehicle-group-vehicles', 'success')
  );
}

export function updateCreateAllVehiclesGroupStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('create-all-vehicles-group', 'success')
  );
}

export function updateSelectedVehicleGroupRoutingProfilesStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus(
      'edit-selected-vehicle-group-routing-profiles',
      'success'
    )
  );
}

export function updateSelectedVehicleGroupMapSetsStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('edit-selected-vehicle-group-map-sets', 'success')
  );
}

export function updateSelectedVehicleGroupRouteModifiersStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus(
      'edit-selected-vehicle-group-route-modifiers',
      'success'
    )
  );
}

export function updateOffset(offset: number) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    offset,
  }));
}

export function updateSort(
  sortExpression: string,
  sortDirection: '' | 'asc' | 'desc'
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    sortExpression,
    sortDirection,
  }));
}

export function updateQuery(query: string) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    query,
  }));
}

export function updatevehicleGroupsRowsDescriptor(
  vehicleGroupsRowsDescriptor: RowsDescriptor
) {
  vehicleGroupsStore.update((state) => ({
    ...state,
    vehicleGroupsRowsDescriptor,
  }));
}

export function resetVehicleGroupsStatus() {
  vehicleGroupsStore.update(updateRequestStatus('vehicle-groups', 'idle'));
}

export function updateSelectedVehicleGroupCustomPlacesStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('edit-selected-vehicle-group-custom-places', 'success')
  );
}

export function updateConfigurationProfileAssignmentStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('configuration-profile-assignment', 'success')
  );
}

export function updateSpeedCoachingProfileAssignmentStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('speed-coaching-profile-assignment', 'success')
  );
}

export function updateDeleteVehicleGroupStatus() {
  vehicleGroupsStore.update(
    updateRequestStatus('delete-vehicle-group', 'success')
  );
}

export function updateVehicleGroupFilterOptions(vehicleGroups: VehicleGroup[]) {
  vehicleGroupsStore.update(
    (state) => ({
      ...state,
      vehicleGroupFilterOptions: vehicleGroups,
    }),
    updateRequestStatus('load-vehicle-group-filter-options', 'success')
  );
}

export const vehicleGroupStoreSnapshot = () =>
  vehicleGroupsStore.getValue() as vehicleGroupsProps;

export const vehicleGroupEntitiesSnapshot = () =>
  vehicleGroupsStore.query(getAllEntities());

export function clearVehicleGroupsStore() {
  emitOnce(() => {
    vehicleGroupsStore.update(clearRequestsStatus());
    vehicleGroupsStore.reset();
  });
}
